import React, { useState, useEffect, useCallback } from 'react';
import { IconCamera } from '../../ui/Icons';

const GalleryImage = ({ src, alt, className }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div className={`gallery-image-placeholder ${className || ''}`}>
        <IconCamera />
      </div>
    );
  }
  return <img className={className} src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
};

const ImageGallery = ({ images, vehicleName }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const openFullscreen = (index) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => setIsFullscreen(false);

  const prev = useCallback(() => {
    setFullscreenIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback(() => {
    setFullscreenIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, prev, next]);

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  const remaining = images.length - 3;

  return (
    <>
      <div className="image-gallery-hero">
        {/* Main large image */}
        <button
          className="hero-main"
          onClick={() => openFullscreen(0)}
          aria-label={`View ${vehicleName} image 1`}
        >
          <GalleryImage src={images[0]} alt={vehicleName} />
        </button>

        {/* Right column — up to 2 tiles */}
        {images.length > 1 && (
          <div className="hero-side">
            {/* Second image */}
            <button
              className="hero-side-tile"
              onClick={() => openFullscreen(1)}
              aria-label={`View ${vehicleName} image 2`}
            >
              <GalleryImage src={images[1]} alt={`${vehicleName} 2`} />
            </button>

            {/* Third image, or "+more" overlay if >3 images */}
            {images.length > 2 && (
              <button
                className="hero-side-tile"
                onClick={() => openFullscreen(2)}
                aria-label={`View all ${images.length} photos`}
              >
                <GalleryImage src={images[2]} alt={`${vehicleName} 3`} />
                {remaining > 0 && (
                  <div className="hero-more-overlay">
                    <span>+{remaining}</span>
                    <small>more photos</small>
                  </div>
                )}
              </button>
            )}
          </div>
        )}

        {/* Badge */}
        <div className="hero-badge"><IconCamera /> {images.length} Photos</div>
      </div>

      {/* Lightbox */}
      {isFullscreen && (
        <div className="lightbox-overlay" onClick={closeFullscreen}>
          <button className="lightbox-close" onClick={closeFullscreen} aria-label="Close">✕</button>

          <button
            className="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <GalleryImage
              src={images[fullscreenIndex]}
              alt={`${vehicleName} ${fullscreenIndex + 1} of ${images.length}`}
            />
            <p className="lightbox-counter">{fullscreenIndex + 1} / {images.length}</p>
          </div>

          <button
            className="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
